<template>
  <v-dialog v-model="dialog" max-width="1200" persistent>
    <v-card>
      <v-toolbar dense flat>
        <v-toolbar-title class="font-weight-bold">
          Bonos de {{ nombreCompleto }}
        </v-toolbar-title>
      </v-toolbar>

      <v-card-text>
        <div v-if="!tieneBonos" class="text-subtitle-2 grey--text">
          Esta persona no tiene bonos asignados.
        </div>

        <div v-else>
          <!-- Tabs por bono -->
          <v-tabs v-model="tab" show-arrows>
            <v-tab v-for="b in personaBonos" :key="bonoKey(b)">
              <span class="mr-2">{{ b.codigo }}</span>
              <span class="font-weight-medium">{{ b.nombre }}</span>
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="tab" class="mt-3">
            <v-tab-item v-for="b in personaBonos" :key="bonoKey(b)">
              <!-- Resumen bono -->
              <v-row dense class="mb-3">
                <v-col cols="12" md="8">
                  <div class="text-subtitle-1 font-weight-bold">
                    {{ b.nombreConceptoBono || b.nombre }}
                  </div>
                  <div class="text-caption grey--text">
                    Asignado: <b>{{ fmtDateTime(b.fechaAsignado) }}</b>
                  </div>
                </v-col>

                <v-col cols="12" md="4" class="d-flex align-center justify-end flex-wrap">
                  <v-chip class="ma-1" outlined color="primary">
                    Total: <b class="ml-1">{{ fmtCurrency(b.total) }}</b>
                  </v-chip>
                  <v-chip class="ma-1" outlined :color="proximoPagoColor(b)">
                    Próximo pago: <b class="ml-1">{{ proximoPagoTexto(b) }}</b>
                  </v-chip>
                  <v-chip class="ma-1" outlined :color="isKpiBono(b) ? 'indigo' : 'blue-grey'">
                    Tipo: <b class="ml-1">{{ getBonoTipoLabel(b) }}</b>
                  </v-chip>
                  <v-chip v-if="isKpiBono(b)" class="ma-1" outlined color="indigo">
                    KPI(s): <b class="ml-1">{{ getBonoKpiNamesText(b) }}</b>
                  </v-chip>
                  <v-chip v-if="isKpiBono(b)" class="ma-1" outlined color="teal">
                    Alcanzado: <b class="ml-1">{{ getBonoKpiAlcanceText(b) }}</b>
                  </v-chip>
                  <v-chip v-if="!isKpiBono(b)" class="ma-1" outlined color="purple">
                    Meta: <b class="ml-1">{{ safeNum(b.meta) }}</b>
                  </v-chip>
                  <v-chip v-if="!isKpiBono(b)" class="ma-1" outlined color="purple">
                    Tolerancia: <b class="ml-1">{{ safeNum(b.tolerancia) }}</b>
                  </v-chip>
                </v-col>
              </v-row>

              <!-- MESES HORIZONTALES (barra de scroll abajo, sin flechas) -->
              <div v-if="!hasCalendario(b)" class="text-caption grey--text">
                No hay calendario de eventos para este bono.
              </div>

              <div v-else style="display:flex; gap:12px; overflow-x:auto; padding-bottom:10px;">
                <v-card v-for="m in (b.calendario || [])" :key="m.mesKey" outlined
                  style="width: 380px; flex: 0 0 auto;">
                  <v-card-title class="py-2">
                    <div
                      style="display:grid; grid-template-columns: 1fr auto; align-items:center; column-gap: 8px; width:100%;">
                      <!-- Mes -->
                      <div style="min-width:0;">
                        <div class="font-weight-bold text-truncate">
                          {{ fmtMesKey(m.mesKey) }}
                        </div>
                      </div>

                      <!-- Chips -->
                      <div style="white-space:nowrap;">
                        <v-chip x-small outlined color="green" class="ml-1">
                          Pag {{ countEstado(m, 'PAGADO') }}
                        </v-chip>
                        <v-chip x-small outlined color="amber" class="ml-1">
                          Pend {{ countEstado(m, 'PENDIENTE') }}
                        </v-chip>
                        <v-chip x-small outlined color="blue-grey" class="ml-1">
                          Den {{ countEstado(m, 'NO_APLICA') }}
                        </v-chip>
                      </div>

                    </div>
                  </v-card-title>

                  <v-divider />

                  <!-- eventos del mes (scroll vertical dentro de cada card) -->
                  <v-card-text class="py-2" style="max-height: 460px; overflow-y:auto;">
                    <div v-if="!(m.eventos && m.eventos.length)" class="text-caption grey--text">
                      Sin eventos en este mes.
                    </div>

                    <div v-for="ev in (m.eventos || [])" :key="ev.periodoKey" class="py-2">
                      <div class="d-flex align-center justify-space-between">
                        <div>
                          <div class="text-caption grey--text">Corte</div>
                          <div class="font-weight-medium">
                            {{ fmtDate(ev.fechaCorte) }}
                          </div>
                        </div>

                        <v-chip dark :color="statusColor(ev.estadoEdit || ev.estado)">
                          <span class="text-subtitle-2 font-weight-bold">
                            {{ formatEstado(ev.estadoEdit || ev.estado) }}
                          </span>
                        </v-chip>

                      </div>

                      <div class="text-caption grey--text mt-1">
                        {{ fmtDateTime(ev.periodoDesde) }} → {{ fmtDateTime(ev.periodoHasta) }}
                      </div>

                      <div class="d-flex align-center mt-1 flex-wrap" v-if="hasEventoSignal(ev, 'calculo_parcial_hasta_hoy') || hasEventoSignal(ev, 'periodo_no_iniciado')">
                        <v-chip x-small outlined color="indigo" class="mr-2 mt-1" v-if="hasEventoSignal(ev, 'calculo_parcial_hasta_hoy')">
                          Avance parcial (al día de hoy)
                        </v-chip>
                        <v-chip x-small outlined color="blue-grey" class="mr-2 mt-1" v-if="hasEventoSignal(ev, 'periodo_no_iniciado')">
                          Periodo no iniciado
                        </v-chip>
                      </div>

                      <!-- resumen rápido -->
                      <div class="d-flex align-center mt-2 flex-wrap">
                        <!-- porcentaje -->
                        <v-chip outlined color="blue" class="mr-2">
                          % alcanzado: <b class="ml-1">{{ fmtPct(getEventoPorcentaje(ev)) }}</b>
                        </v-chip>

                        <!-- monto a pagar (calculado) -->
                        <v-chip outlined color="primary" class="mr-2">
                          Monto estimado al día: <b class="ml-1">{{ fmtCurrency(getEventoMontoEstimado(ev, b)) }}</b>
                        </v-chip>

                        <v-chip outlined :color="statusColor(ev.estadoEdit || ev.estado)" class="mr-2">
                          Estado: <b class="ml-1">{{ formatEstado(ev.estadoEdit || ev.estado) }}</b>
                        </v-chip>

                        <!-- pagado -->
                        <v-chip v-if="isEstado(ev, 'PAGADO')" x-small outlined color="success" class="mr-2 mt-3">
                          Pagado: <b class="ml-1">{{ fmtCurrency(Number(ev.montoEdit || 0)) }}</b>
                        </v-chip>

                        <!-- motivo no pagar -->
                        <v-chip v-if="ev.noPagarMarcado" x-small outlined color="amber" class="mr-2 mt-3">
                          Motivo: <b class="ml-1">
                            {{ (ev.motivoEdit || '').slice(0, 22) }}{{ (ev.motivoEdit || '').length > 22 ? '…' : '' }}
                          </b>
                        </v-chip>

                        <v-chip v-if="hasTrazabilidad(ev)" x-small outlined color="indigo" class="mr-2 mt-3">
                          Score base: <b class="ml-1">{{ safeNum(getTrazaNum(ev, "scoreBase")) }}</b>
                        </v-chip>

                        <v-chip v-if="hasTrazabilidad(ev)" x-small outlined color="indigo" class="mr-2 mt-3">
                          Score final: <b class="ml-1">{{ safeNum(getTrazaNum(ev, "scoreFinal")) }}</b>
                        </v-chip>

                        <v-chip v-if="hasTrazabilidad(ev)" x-small outlined color="indigo" class="mr-2 mt-3">
                          Umbral: <b class="ml-1">{{ safeNum(getTrazaNum(ev, "umbral")) }}</b>
                        </v-chip>

                        <v-chip v-if="getTipoCalculoEvento(ev)" x-small outlined color="teal" class="mr-2 mt-3">
                          Tipo cálculo: <b class="ml-1">{{ getTipoCalculoEvento(ev) }}</b>
                        </v-chip>

                        <v-chip v-if="getSenalesEvento(ev)" x-small outlined color="deep-purple" class="mr-2 mt-3">
                          Señales: <b class="ml-1">{{ getSenalesEvento(ev) }}</b>
                        </v-chip>
                      </div>

                      <div v-if="hasEventoKpiResultados(ev)" class="mt-2">
                        <div class="text-caption grey--text mb-1">Avance KPI al día</div>
                        <div class="kpi-grid">
                          <v-card
                            v-for="(kr, i) in getEventoKpiResultados(ev)"
                            :key="(kr.kpi || 'KPI') + '_' + i"
                            outlined
                            class="pa-2 kpi-card"
                          >
                            <div class="d-flex align-center justify-space-between">
                              <div class="font-weight-bold text-caption text-truncate mr-2">{{ kr.kpi || "N/A" }}</div>
                              <v-chip x-small outlined :color="kpiEstadoColor(kr)">
                                {{ kpiEstadoLabel(kr) }}
                              </v-chip>
                            </div>
                            <div class="text-caption mt-1 kpi-muted">
                              Valor: <b>{{ fmtKpiValor(kr.valor) }}</b>
                            </div>
                            <div class="text-caption kpi-muted">
                              Regla: <b>{{ fmtKpiRegla(kr) }}</b>
                            </div>
                            <div class="text-caption kpi-muted">
                              Peso: <b>{{ fmtPct(kr.peso) }}</b>
                            </div>
                          </v-card>
                        </div>
                      </div>


                      <!-- Acciones (abren dialog) -->
                      <div class="d-flex align-center mt-2">
                        <template v-if="!isFinal(ev)">
                          <v-btn x-small outlined class="mr-2" :color="isEstado(ev, 'PAGADO') ? 'green' : 'grey'"
                            :disabled="!canAccionarEvento(ev)"
                            @click="abrirDialogPagar(ev, b)">
                            <v-icon left small>mdi-cash-check</v-icon>
                            Pagar
                          </v-btn>

                          <v-btn x-small outlined :color="ev.noPagarMarcado ? 'amber' : 'grey'"
                            :disabled="!canAccionarEvento(ev)"
                            @click="abrirDialogNoPagar(ev, b)">
                            <v-icon left small>mdi-close-circle-outline</v-icon>
                            No pagar
                          </v-btn>
                        </template>

                        <v-spacer />

                        <v-tooltip bottom v-if="!eventoValido(ev)">
                          <template v-slot:activator="{ on, attrs }">
                            <v-icon v-bind="attrs" v-on="on" color="red" small>
                              mdi-alert
                            </v-icon>
                          </template>
                          <span>{{ mensajeInvalido(ev) }}</span>
                        </v-tooltip>

                      </div>

                      <div v-if="!canAccionarEvento(ev)" class="text-caption blue-grey--text mt-1">
                        <v-icon small color="blue-grey" class="mr-1">mdi-clock-outline</v-icon>
                        {{ mensajePeriodoNoDisponible(ev) }}
                      </div>


                      <v-divider class="mt-3" />
                    </div>
                  </v-card-text>
                </v-card>
              </div>
            </v-tab-item>
          </v-tabs-items>
        </div>
      </v-card-text>

      <v-card-actions>
        <v-spacer />
        <v-btn text color="success" @click="cerrar">Cerrar</v-btn>
        <!-- <v-btn text color="primary" :disabled="!todoValido" :loading="loadingGuardar" @click="guardarCambios">
          Guardar
        </v-btn> -->
      </v-card-actions>

      <!-- ===== DIALOG PAGAR ===== -->
      <v-dialog v-model="dialogPagar" max-width="420" persistent>
        <v-card>
          <v-card-title class="text-h6">Pagar</v-card-title>
          <v-card-text>
            <!-- <div class="text-caption grey--text">Bono</div>
            <div class="font-weight-medium">{{ dlgBono ? (dlgBono.nombre || dlgBono.codigo) : '—' }}</div> -->

            <div class="text-caption grey--text mt-2">Corte</div>
            <div class="font-weight-medium">{{ dlgEvento ? fmtDate(dlgEvento.fechaCorte) : '—' }}</div>

            <div class="text-caption grey--text mt-2">Porcentaje alcanzado</div>
            <div class="font-weight-medium">{{ dlgEvento ? dlgEvento.porcentaje : '—' }} %</div>

            <v-text-field class="mt-3" v-model.number="dlgMonto" label="Monto a pagar" prefix="$" type="number" outlined
              dense :error="dlgMontoError" :hint="dlgMontoHint" persistent-hint :max="dlgMontoMax"
              :loading="loadingCaptura" />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text color="success" @click="cerrarDlgPagar" :loading="loadingCaptura">Cancelar</v-btn>
            <v-btn text color="error" @click="confirmarPagar" :disabled="dlgMontoError" :loading="loadingCaptura">
              Confirmar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- ===== DIALOG NO PAGAR ===== -->
      <v-dialog v-model="dlgNoPagar" max-width="520" persistent>
        <v-card>
          <v-card-title class="text-h6">No pagar</v-card-title>
          <v-card-text>
            <div class="text-caption grey--text">Bono</div>
            <div class="font-weight-medium">{{ dlgBono ? (dlgBono.nombre || dlgBono.codigo) : '—' }}</div>

            <div class="text-caption grey--text mt-2">Corte</div>
            <div class="font-weight-medium">{{ dlgEvento ? fmtDate(dlgEvento.fechaCorte) : '—' }}</div>

            <v-textarea class="mt-3" v-model="dlgMotivo" label="Motivo por el que no se pagará" outlined dense rows="3"
              auto-grow :error="dlgMotivoError" :hint="dlgMotivoError ? 'Motivo requerido' : ''" persistent-hint
              :loading="loadingCaptura" />
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text color="success" @click="cerrarDlgNoPagar" :loading="loadingCaptura">Cancelar</v-btn>
            <v-btn text color="error" @click="confirmarNoPagar" :disabled="dlgMotivoError" :loading="loadingCaptura">
              Confirmar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';

export default {
  props: {
    value: { type: Boolean, default: false },
    persona: { type: Object, default: null },
  },
  data: () => ({
    tab: 0,
    loadingGuardar: false,

    dialogPagar: false,
    dlgNoPagar: false,
    dlgEvento: null,
    dlgBono: null,
    dlgMonto: 0,
    dlgMotivo: "",

    loadingCaptura: false,
  }),
  computed: {
    dialog: {
      get() { return this.value },
      set(v) { this.$emit("input", v) },
    },
    personaBonos() {
      return (this.persona && Array.isArray(this.persona.bonos)) ? this.persona.bonos : []
    },
    tieneBonos() {
      return this.personaBonos.length > 0
    },
    nombreCompleto() {
      const p = this.persona || {}
      return [p.nombre, p.paterno, p.materno].filter(Boolean).join(" ")
    },

    todoValido() {
      const bonos = this.personaBonos || []
      for (let i = 0; i < bonos.length; i++) {
        const b = bonos[i]
        const cal = Array.isArray(b.calendario) ? b.calendario : []
        for (let j = 0; j < cal.length; j++) {
          const m = cal[j]
          const evs = Array.isArray(m.eventos) ? m.eventos : []
          for (let k = 0; k < evs.length; k++) {
            const ev = evs[k]
            if (!this.eventoValido(ev)) return false
          }
        }
      }
      return true
    },

    dlgMontoError() {
      const n = Number(this.dlgMonto)
      if (!(Number.isFinite(n) && n > 0)) return true
      const max = Number(this.dlgMontoMax)
      if (Number.isFinite(max) && n > max) return true
      return false
    },
    dlgMontoMax() {
      return this.getMaxMontoPagar(this.dlgEvento, this.dlgBono)
    },
    dlgMontoHint() {
      const max = Number(this.dlgMontoMax)
      if (!(Number.isFinite(max) && max >= 0)) return "No hay monto máximo disponible para este evento."
      if (max === 0) return "Este evento no permite pago (máximo 0)."
      if (this.dlgMontoError) return "El monto debe ser mayor a 0 y no exceder " + this.fmtCurrency(max)
      return "Máximo permitido: " + this.fmtCurrency(max)
    },
    dlgMotivoError() {
      return !(String(this.dlgMotivo || "").trim().length > 0)
    },
  },
  watch: {
    value(v) {
      if (v) {
        this.tab = 0
        this.prepararEdicionBonos()
      }
    },
    persona() {
      this.tab = 0
      if (this.dialog) this.prepararEdicionBonos()
    },
  },
  methods: {
    parseJsonSafe(raw) {
      if (!raw) return null
      if (typeof raw === "object") return raw
      if (typeof raw !== "string") return null
      try {
        return JSON.parse(raw)
      } catch (e) {
        return null
      }
    },
    findTrazaObject(ev) {
      if (!ev || typeof ev !== "object") return {}

      const directCandidates = [
        ev.trazabilidadCalculo,
        ev.TrazabilidadCalculo,
        ev.trazabilidad,
        ev.Trazabilidad,
        ev.trace,
        ev.Trace,
        ev.calculo,
        ev.Calculo,
      ]
      for (let i = 0; i < directCandidates.length; i++) {
        const obj = this.parseJsonSafe(directCandidates[i])
        if (obj && typeof obj === "object") return obj
      }

      const motivo = String(ev.motivoNoAplica || ev.MotivoNoAplica || ev.motivo || "")
      const start = motivo.indexOf("{")
      const end = motivo.lastIndexOf("}")
      if (start >= 0 && end > start) {
        const parsed = this.parseJsonSafe(motivo.slice(start, end + 1))
        if (parsed && typeof parsed === "object") return parsed
      }

      return {}
    },
    getTrazaValue(ev, key) {
      const tr = this.findTrazaObject(ev)
      if (!tr || typeof tr !== "object") return null

      const aliases = {
        scoreBase: ["scoreBase", "ScoreBase"],
        scoreFinal: ["scoreFinal", "ScoreFinal"],
        umbral: ["umbral", "Umbral", "threshold", "Threshold"],
        tipoCalculo: ["tipoCalculo", "TipoCalculo", "tipo_calculo"],
        senales: ["senales", "Señales", "signals", "Signals"]
      }
      const names = aliases[key] || [key]
      for (let i = 0; i < names.length; i++) {
        const n = names[i]
        if (tr[n] !== undefined && tr[n] !== null) return tr[n]
      }
      return null
    },
    getTrazaNum(ev, key) {
      const v = this.getTrazaValue(ev, key)
      const n = Number(v)
      return Number.isFinite(n) ? n : null
    },
    getTrazabilidadCalculo(ev) {
      const direct = this.parseJsonSafe(ev && (ev.trazabilidadCalculo || ev.TrazabilidadCalculo))
      if (direct && typeof direct === "object") return direct
      return this.findTrazaObject(ev) || {}
    },
    getEventoSignals(ev) {
      const tr = this.getTrazabilidadCalculo(ev)
      const fromTr = tr && tr.signals
      const fromGeneric = this.getTrazaValue(ev, "senales")
      const raw = fromTr !== undefined ? fromTr : fromGeneric
      if (Array.isArray(raw)) return raw.map(x => String(x || "").trim()).filter(Boolean)
      if (typeof raw === "string") return raw.split(",").map(x => x.trim()).filter(Boolean)
      return []
    },
    hasEventoSignal(ev, signalName) {
      const target = String(signalName || "").trim().toLowerCase()
      if (!target) return false
      return this.getEventoSignals(ev).some(s => String(s || "").trim().toLowerCase() === target)
    },
    getEventoKpiResultados(ev) {
      const tr = this.getTrazabilidadCalculo(ev)
      const rows = tr && Array.isArray(tr.kpiResultados) ? tr.kpiResultados : []
      return rows.map((r) => ({
        kpi: r && (r.kpi || r.Kpi),
        valor: r && (r.valor !== undefined ? r.valor : r.Valor),
        operador: r && (r.operador || r.Operador),
        metaMinima: r && (r.metaMinima !== undefined ? r.metaMinima : r.MetaMinima),
        peso: r && (r.peso !== undefined ? r.peso : r.Peso),
        cumple: r && (r.cumple !== undefined ? r.cumple : r.Cumple),
        sinDato: r && (r.sinDato !== undefined ? r.sinDato : r.SinDato),
      }))
    },
    hasEventoKpiResultados(ev) {
      return this.getEventoKpiResultados(ev).length > 0
    },
    getEventoPorcentaje(ev) {
      if (this.hasEventoSignal(ev, "periodo_no_iniciado")) return 0
      const n = Number(ev && ev.porcentaje)
      if (Number.isFinite(n)) return n
      return this.getPorcentaje(ev)
    },
    getEventoMontoEstimado(ev, bono) {
      const n = Number(ev && ev.montoAPagar)
      if (Number.isFinite(n)) return n
      return this.getMontoAPagar(ev, bono)
    },
    fmtPct(v) {
      const n = Number(v)
      if (!Number.isFinite(n)) return "0%"
      const rounded = Math.round(n * 100) / 100
      return rounded.toLocaleString("es-MX", { minimumFractionDigits: 1, maximumFractionDigits: 2 }) + "%"
    },
    fmtKpiValor(v) {
      if (v === null || v === undefined || v === "") return "Sin dato"
      const n = Number(v)
      if (!Number.isFinite(n)) return String(v)
      return n.toLocaleString("es-MX", { minimumFractionDigits: 1, maximumFractionDigits: 2 })
    },
    fmtKpiRegla(kr) {
      const op = String((kr && kr.operador) || "")
      const meta = kr && kr.metaMinima
      return (op && meta !== undefined && meta !== null) ? (op + " " + meta) : "N/A"
    },
    kpiEstadoLabel(kr) {
      if (!kr || kr.sinDato === true || kr.valor === null || kr.valor === undefined || kr.valor === "") return "Sin dato"
      if (kr.cumple === true) return "Cumple"
      if (kr.cumple === false) return "No cumple"
      return "Sin dato"
    },
    kpiEstadoColor(kr) {
      const s = this.kpiEstadoLabel(kr)
      if (s === "Cumple") return "green"
      if (s === "No cumple") return "red"
      return "grey"
    },
    hasTrazabilidad(ev) {
      return this.getTrazaNum(ev, "scoreBase") !== null ||
        this.getTrazaNum(ev, "scoreFinal") !== null ||
        this.getTrazaNum(ev, "umbral") !== null
    },
    getTipoCalculoEvento(ev) {
      const tr = this.getTrazaValue(ev, "tipoCalculo")
      const direct = ev && (ev.tipoCalculo || ev.TipoCalculo)
      const v = tr || direct
      if (!v) return ""
      return String(v).toUpperCase().replace(/_/g, " ")
    },
    getSenalesEvento(ev) {
      const v = this.getTrazaValue(ev, "senales")
      if (!v) return ""
      if (Array.isArray(v)) return v.join(", ")
      if (typeof v === "object") {
        const keys = Object.keys(v)
        return keys.map(k => `${k}:${v[k]}`).join(", ")
      }
      return String(v)
    },
    bonoKey(b) {
      return String(b.idOperadorBono || b.idBono || (b.codigo || "") + "_" + (b.nombre || ""))
    },

    cerrar() {
      this.$emit("reload");
      this.dialog = false;
    },

    hasCalendario(b) {
      return b && Array.isArray(b.calendario) && b.calendario.length > 0
    },

    // ====== edición base ======
    prepararEdicionBonos() {
      const bonos = this.personaBonos || []

      bonos.forEach((b) => {
        const cal = Array.isArray(b.calendario) ? b.calendario : []

        cal.forEach((m) => {
          const evs = Array.isArray(m.eventos) ? m.eventos : []

          evs.forEach((ev) => {
            if (!ev) return

            const estadoOrig = String(ev.estado || "").toUpperCase()

            // estadoEdit
            if (ev.estadoEdit === undefined) this.$set(ev, "estadoEdit", ev.estado)

            // motivoEdit
            const motivoBase = ev.motivoNoAplica ?? ev.motivo ?? ""
            if (ev.motivoEdit === undefined) this.$set(ev, "motivoEdit", motivoBase)
            else if (estadoOrig === "NO_APLICA" && !String(ev.motivoEdit || "").trim()) {
              this.$set(ev, "motivoEdit", motivoBase)
            }

            // noPagarMarcado (si viene NO_APLICA del back, debe quedar marcado)
            if (ev.noPagarMarcado === undefined) {
              this.$set(ev, "noPagarMarcado", estadoOrig === "NO_APLICA")
            } else if (estadoOrig === "NO_APLICA" && ev.noPagarMarcado !== true) {
              this.$set(ev, "noPagarMarcado", true)
            }

            // montoEdit
            if (ev.montoEdit === undefined) {
              const direct =
                ev.montoPagado !== undefined ? Number(ev.montoPagado)
                  : (ev.monto !== undefined ? Number(ev.monto) : NaN)

              let monto = 0
              if (Number.isFinite(direct)) monto = direct
              else monto = (estadoOrig === "PAGADO") ? Number(b.total || 0) : 0

              // NO_APLICA siempre 0
              if (estadoOrig === "NO_APLICA") monto = 0

              this.$set(ev, "montoEdit", monto)
            } else if (estadoOrig === "NO_APLICA" && Number(ev.montoEdit || 0) !== 0) {
              this.$set(ev, "montoEdit", 0)
            }

            // snapshot para cambios
            if (ev._estadoOrig === undefined) this.$set(ev, "_estadoOrig", ev.estado)
            if (ev._montoOrig === undefined) this.$set(ev, "_montoOrig", Number(ev.montoEdit || 0))
            if (ev._motivoOrig === undefined) this.$set(ev, "_motivoOrig", ev.motivoEdit || "")
            if (ev._noPagarOrig === undefined) this.$set(ev, "_noPagarOrig", !!ev.noPagarMarcado)
          })
        })
      })
    },


    // ====== dialogs ======
    abrirDialogPagar(ev, bono) {
      if (!this.canAccionarEvento(ev)) return
      this.dlgEvento = ev
      this.dlgBono = bono

      const max = this.getMaxMontoPagar(ev, bono)
      this.dlgMonto = Number.isFinite(max) && max > 0 ? max : 0

      this.dialogPagar = true
    },
    cerrarDlgPagar() {
      this.dialogPagar = false
      this.dlgEvento = null
      this.dlgBono = null
      this.dlgMonto = 0
    },
    async confirmarPagar() {
      if (!this.dlgEvento || !this.dlgBono) return
      if (!this.canAccionarEvento(this.dlgEvento)) return
      if (this.dlgMontoError) return

      const idOperadorBono = Number(this.dlgBono.idOperadorBono ?? this.dlgBono.idBono)
      const periodoKey = Number(this.dlgEvento.periodoKey ?? this.dlgEvento.PeriodoKey)
      const monto = Number(this.dlgMonto)
      const max = Number(this.dlgMontoMax)

      if (!Number.isFinite(idOperadorBono) || idOperadorBono <= 0) return
      if (!Number.isFinite(periodoKey) || periodoKey <= 0) return
      if (!Number.isFinite(monto) || monto <= 0) return
      if (Number.isFinite(max) && monto > max) return

      const vm = {
        IdOperadorBono: idOperadorBono,
        PeriodoKey: periodoKey,
        Aplica: true,
        MontoPagado: monto,
        MotivoNoAplica: ""
      }

      this.loadingCaptura = true
      try {
        await axios.post("Bonos/CapturarPagoBono", vm)

        // reflejar en UI
        this.$set(this.dlgEvento, "estadoEdit", "PAGADO")
        this.$set(this.dlgEvento, "montoAPagar", monto)
        this.$set(this.dlgEvento, "montoEdit", monto)
        this.$set(this.dlgEvento, "noPagarMarcado", false)
        this.$set(this.dlgEvento, "motivoEdit", "")

        // marcar como “ya guardado” para que no cuente como cambio pendiente
        this.$set(this.dlgEvento, "_estadoOrig", "PAGADO")
        this.$set(this.dlgEvento, "_montoOrig", monto)
        this.$set(this.dlgEvento, "_motivoOrig", "")
        this.$set(this.dlgEvento, "_noPagarOrig", false)

        this.cerrarDlgPagar()
      } catch (e) {
        console.log(e)
      } finally {
        this.loadingCaptura = false
      }
    },

    abrirDialogNoPagar(ev, bono) {
      if (!this.canAccionarEvento(ev)) return
      this.dlgEvento = ev
      this.dlgBono = bono
      this.dlgMotivo = String((ev && ev.motivoEdit) || "")
      this.dlgNoPagar = true
    },
    cerrarDlgNoPagar() {
      this.dlgNoPagar = false
      this.dlgEvento = null
      this.dlgBono = null
      this.dlgMotivo = ""
    },
    async confirmarNoPagar() {
      if (!this.dlgEvento || !this.dlgBono) return
      if (!this.canAccionarEvento(this.dlgEvento)) return
      if (this.dlgMotivoError) return

      const idOperadorBono = Number(this.dlgBono.idOperadorBono ?? this.dlgBono.idBono)
      const periodoKey = Number(this.dlgEvento.periodoKey ?? this.dlgEvento.PeriodoKey)
      const motivo = String(this.dlgMotivo || "").trim()

      if (!Number.isFinite(idOperadorBono) || idOperadorBono <= 0) return
      if (!Number.isFinite(periodoKey) || periodoKey <= 0) return
      if (!motivo.length) return

      const vm = {
        IdOperadorBono: idOperadorBono,
        PeriodoKey: periodoKey,
        Aplica: false,
        MontoPagado: 0,
        MotivoNoAplica: motivo
      }

      this.loadingCaptura = true
      try {
        await axios.post("Bonos/CapturarPagoBono", vm)

        // reflejar en UI
        this.$set(this.dlgEvento, "estadoEdit", "NO_APLICA")
        this.$set(this.dlgEvento, "montoAPagar", 0)
        this.$set(this.dlgEvento, "montoEdit", 0)
        this.$set(this.dlgEvento, "noPagarMarcado", true)
        this.$set(this.dlgEvento, "motivoEdit", motivo)

        // marcar como “ya guardado”
        this.$set(this.dlgEvento, "_estadoOrig", "NO_APLICA")
        this.$set(this.dlgEvento, "_montoOrig", 0)
        this.$set(this.dlgEvento, "_motivoOrig", motivo)
        this.$set(this.dlgEvento, "_noPagarOrig", true)

        this.cerrarDlgNoPagar()
      } catch (e) {
        console.log(e)
      } finally {
        this.loadingCaptura = false
      }
    },

    // ====== validación evento ======
    isEstado(ev, estado) {
      const e = String((ev && (ev.estadoEdit || ev.estado)) || "").toUpperCase()
      return e === String(estado).toUpperCase()
    },
    eventoValido(ev) {
      // PAGADO => monto > 0
      if (this.isEstado(ev, "PAGADO")) {
        const n = Number(ev && ev.montoEdit)
        return Number.isFinite(n) && n > 0
      }

      // NO_APLICA => motivo requerido
      if (this.isEstado(ev, "NO_APLICA") || (ev && ev.noPagarMarcado)) {
        return String(ev && ev.motivoEdit || "").trim().length > 0
      }

      // PENDIENTE => ok
      return true
    },

    mensajeInvalido(ev) {
      if (this.isEstado(ev, "PAGADO")) return "Monto requerido para pagar."
      if (ev && ev.noPagarMarcado) return "Motivo requerido para no pagar."
      return "Revisar datos."
    },

    countEstado(mes, estado) {
      const evs = (mes && Array.isArray(mes.eventos)) ? mes.eventos : []
      const target = String(estado).toUpperCase()
      return evs.filter(x => String((x.estadoEdit || x.estado) || "").toUpperCase() === target).length
    },

    getCambios() {
      const cambios = []
      const bonos = this.personaBonos || []

      bonos.forEach((b) => {
        const cal = Array.isArray(b.calendario) ? b.calendario : []
        cal.forEach((m) => {
          const evs = Array.isArray(m.eventos) ? m.eventos : []
          evs.forEach((ev) => {
            const cambioEstado = ev.estadoEdit !== ev._estadoOrig
            const cambioMonto = Number(ev.montoEdit || 0) !== Number(ev._montoOrig || 0)
            const cambioMotivo = String(ev.motivoEdit || "") !== String(ev._motivoOrig || "")
            const cambioNoPagar = !!ev.noPagarMarcado !== !!ev._noPagarOrig

            if (cambioEstado || cambioMonto || cambioMotivo || cambioNoPagar) {
              cambios.push({
                idBono: b.idBono,
                periodoKey: ev.periodoKey,
                estado: ev.estadoEdit,
                monto: Number(ev.montoEdit || 0),
                motivo: String(ev.motivoEdit || ""),
                noPagar: !!ev.noPagarMarcado,
              })
            }
          })
        })
      })

      return cambios
    },

    async guardarCambios() {
      const payload = {
        idPersona: this.persona ? this.persona.idPersona : null,
        cambios: this.getCambios(),
      }
      if (!payload.cambios.length) return
      if (!this.todoValido) return

      this.loadingGuardar = true
      try {
        await this.$emit("guardar", payload)
        this.prepararEdicionBonos()
      } finally {
        this.loadingGuardar = false
      }
    },

    // ====== utils ======
    safeNum(v) {
      const n = Number(v)
      return Number.isFinite(n) ? n : 0
    },
    statusColor(estado) {
      const e = String(estado || "").toUpperCase()
      if (e === "PAGADO") return "green"
      if (e === "PENDIENTE") return "amber"
      if (e === "NO_APLICA" || e === "NO APLICA") return "blue-grey"
      if (e === "ANULADO" || e === "CANCELADO") return "red"
      return "grey"
    },
    getPorcentaje(ev) {
      const p = ev && (ev.porcentajeEdit !== undefined ? ev.porcentajeEdit : ev.porcentaje)
      const n = Number(p)
      if (Number.isFinite(n)) return n
      const scoreFinal = this.getTrazaNum(ev, "scoreFinal")
      return scoreFinal !== null ? scoreFinal : 100
    },
    formatEstado(estado) {
      const e = String(estado || "").toUpperCase()
      if (e === "NO_APLICA" || e === "NO APLICA") return "DENEGADO"
      return e || "—"
    },
    isFinal(ev) {
      return this.isEstado(ev, "PAGADO") || this.isEstado(ev, "NO_APLICA")
    },

    getMontoAPagar(ev, bono) {
      // PAGADO => montoEdit
      if (this.isEstado(ev, "PAGADO")) return Number(ev && ev.montoEdit || 0)

      // NO_APLICA => 0
      if (this.isEstado(ev, "NO_APLICA") || (ev && ev.noPagarMarcado)) return 0

      // PENDIENTE => sugerir monto del bono (total)
      return Number(bono && bono.total || 0)
    },
    getMaxMontoPagar(ev, bono) {
      const montoEvento = Number(ev && ev.montoAPagar)
      if (Number.isFinite(montoEvento) && montoEvento > 0) return montoEvento

      const total = Number(bono && bono.total)
      const porcentaje = Number(this.getPorcentaje(ev))
      if (Number.isFinite(total) && total > 0 && Number.isFinite(porcentaje) && porcentaje >= 0) {
        const calc = total * (porcentaje / 100)
        return calc > 0 ? calc : 0
      }

      const sugerido = Number(this.getMontoAPagar(ev, bono))
      if (Number.isFinite(sugerido) && sugerido > 0) return sugerido
      return 0
    },

    fmtCurrency(v) {
      const n = Number(v)
      const val = Number.isFinite(n) ? n : 0
      return val.toLocaleString("es-MX", { style: "currency", currency: "MXN" })
    },
    fmtMesKey(mesKey) {
      const mk = Number(mesKey)
      if (!Number.isFinite(mk)) return String(mesKey || "")

      const y = Math.floor(mk / 100)
      const m = (mk % 100) - 1
      const d = new Date(y, m, 1)

      const txt = d.toLocaleString("es-MX", { month: "long", year: "numeric" }) // mes completo
      return txt.charAt(0).toUpperCase() + txt.slice(1) // primera mayúscula
    },
    fmtDate(iso) {
      if (!iso) return "—"
      const d = new Date(iso)
      if (isNaN(d.getTime())) return String(iso)
      return d.toLocaleDateString("es-MX")
    },
    fmtDateTime(iso) {
      let soloFecha = true;

      if (!iso) return "—";
      const d = new Date(iso);
      if (isNaN(d.getTime())) return String(iso);

      return soloFecha
        ? d.toLocaleDateString("es-MX")
        : d.toLocaleString("es-MX");
    },
    toDateSafe(v) {
      if (!v) return null
      const d = new Date(v)
      return isNaN(d.getTime()) ? null : d
    },
    getFechaLiberacionEvento(ev) {
      if (!ev || typeof ev !== "object") return null
      // Regla: se puede accionar cuando termina el periodo; fallback a fecha de corte.
      return this.toDateSafe(ev.periodoHasta) ||
        this.toDateSafe(ev.fechaCorte) ||
        this.toDateSafe(ev.periodoDesde)
    },
    canAccionarEvento(ev) {
      const fecha = this.getFechaLiberacionEvento(ev)
      if (!fecha) return true
      return new Date() >= fecha
    },
    mensajePeriodoNoDisponible(ev) {
      const fecha = this.getFechaLiberacionEvento(ev)
      if (!fecha) return "Aún no está disponible para capturar."
      return "Disponible a partir de " + this.fmtDateTime(fecha)
    },
    getFechaEvento(ev) {
      if (!ev || typeof ev !== "object") return null
      return this.toDateSafe(ev.fechaCorte) ||
        this.toDateSafe(ev.periodoHasta) ||
        this.toDateSafe(ev.periodoDesde)
    },
    getEventosBono(b) {
      const cal = b && Array.isArray(b.calendario) ? b.calendario : []
      return cal.reduce((acc, m) => {
        if (m && Array.isArray(m.eventos)) acc.push(...m.eventos)
        return acc
      }, [])
    },
    getProximoEventoPendiente(b) {
      const now = new Date()
      const pendientes = this.getEventosBono(b)
        .filter(ev => {
          const estado = String((ev && (ev.estadoEdit || ev.estado)) || "").toUpperCase()
          return estado === "PENDIENTE"
        })
        .map(ev => ({ ev, fecha: this.toDateSafe(ev && ev.fechaCorte) }))
        .filter(x => x.fecha)
        .sort((a, z) => a.fecha - z.fecha)

      if (!pendientes.length) return null

      const futuro = pendientes.find(x => x.fecha >= now)
      return futuro || pendientes[0]
    },
    proximoPagoTexto(b) {
      const item = this.getProximoEventoPendiente(b)
      if (!item) return "No disponible"

      const now = new Date()
      if (item.fecha < now) return "Vencido " + this.fmtDate(item.fecha)
      return this.fmtDate(item.fecha)
    },
    proximoPagoColor(b) {
      const item = this.getProximoEventoPendiente(b)
      if (!item) return "grey"
      const now = new Date()
      return item.fecha < now ? "deep-orange" : "amber"
    },
    getBonoKpiConfig(bono) {
      if (!bono || typeof bono !== "object") return null
      const candidates = [
        bono.kpiConfig,
        bono.configuracionKpi,
        bono.configKpi,
        bono.kpiRulesJson
      ]
      for (let i = 0; i < candidates.length; i++) {
        const parsed = this.parseJsonSafe(candidates[i])
        if (parsed && typeof parsed === "object") return parsed
      }
      return null
    },
    isKpiBono(bono) {
      if (!bono || typeof bono !== "object") return false
      if (bono.usarKpiBuilder === true) return true
      const cfg = this.getBonoKpiConfig(bono)
      if (!cfg) return false
      const rules = Array.isArray(cfg.rules) ? cfg.rules : []
      const kpis = Array.isArray(cfg.kpis) ? cfg.kpis : []
      return rules.length > 0 || kpis.length > 0 || String(cfg.tipo || "").toUpperCase() === "KPI_OPERATIVO"
    },
    getBonoTipoLabel(bono) {
      return this.isKpiBono(bono) ? "KPI Operativo" : "General"
    },
    getBonoKpiNames(bono) {
      const cfg = this.getBonoKpiConfig(bono) || {}
      const fromKpis = Array.isArray(cfg.kpis) ? cfg.kpis : []
      const fromRules = Array.isArray(cfg.rules) ? cfg.rules.map(r => String(r.kpi || "").toUpperCase()).filter(Boolean) : []
      return Array.from(new Set([].concat(fromKpis, fromRules)))
    },
    getBonoKpiNamesText(bono) {
      const names = this.getBonoKpiNames(bono)
      return names.length ? names.join(", ") : "N/A"
    },
    getBonoKpiAlcanceText(bono) {
      const eventos = this.getEventosBono(bono)
      if (!eventos.length) return "N/A"

      const now = new Date()
      const ranked = eventos
        .map(ev => {
          const fecha = this.getFechaEvento(ev)
          const porcentaje = Number(this.getPorcentaje(ev))
          return { fecha, porcentaje }
        })
        .filter(x => x.fecha && Number.isFinite(x.porcentaje))
        .sort((a, z) => z.fecha - a.fecha)

      if (!ranked.length) return "N/A"
      const vigente = ranked.find(x => x.fecha <= now) || ranked[0]
      return String(Math.round(vigente.porcentaje * 100) / 100) + "%"
    },
  },
}
</script>

<style scoped>
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 8px;
}

.kpi-card {
  min-height: 88px;
}

.kpi-muted {
  line-height: 1.2;
}

@media (max-width: 600px) {
  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px;
  }

  .kpi-card {
    min-height: 82px;
    padding: 6px !important;
  }
}
</style>
